import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnChanges, SimpleChanges, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type TextareaSize = 'small' | 'medium' | 'large';

@Component({
  selector: 'app-textarea',
  templateUrl: './textarea.component.html',
  styleUrls: ['./textarea.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextareaComponent),
      multi: true
    }
  ]
})
export class TextareaComponent implements ControlValueAccessor, OnChanges {
  @ViewChild('textarea') textareaElement!: ElementRef<HTMLTextAreaElement>;

  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() value: string = '';
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Input() required: boolean = false;
  @Input() error: boolean = false;
  @Input() size: TextareaSize = 'medium';
  @Input() rows: number = 4;
  @Input() maxLength: number = 0;
  @Input() errorMessage: string = '';

  textareaId: string = `textarea-${Math.random().toString(36).substr(2, 9)}`;

  @Output() valueChange = new EventEmitter<string>();
  @Output() change = new EventEmitter<string>();
  @Output() focus = new EventEmitter<void>();
  @Output() blur = new EventEmitter<void>();

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] && this.textareaElement) {
      this.textareaElement.nativeElement.value = this.value;
    }
  }

  onInputChange(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    this.value = textarea.value;
    this.onChange(this.value);
    this.valueChange.emit(this.value);
    this.change.emit(this.value);
  }

  onFocus(): void {
    this.focus.emit();
  }

  onBlur(): void {
    this.onTouched();
    this.blur.emit();
  }

  clearInput(): void {
    this.value = '';
    this.onChange(this.value);
    this.valueChange.emit(this.value);
    this.change.emit(this.value);
    if (this.textareaElement) {
      this.textareaElement.nativeElement.focus();
    }
  }

  writeValue(value: string): void {
    this.value = value || '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  getTextareaClasses(): string {
    const classes = ['textarea'];
    if (this.size) classes.push(`size-${this.size}`);
    if (this.disabled) classes.push('disabled');
    if (this.readonly) classes.push('readonly');
    if (this.error) classes.push('error');
    if (this.required) classes.push('required');
    return classes.join(' ');
  }

  getCharacterCount(): number {
    return this.value ? this.value.length : 0;
  }

  isCharacterLimitReached(): boolean {
    return this.maxLength > 0 && this.getCharacterCount() >= this.maxLength;
  }
}
